import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Save, Trash2, LayoutTemplate } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const FIELDS = ["subject", "subject_fr", "message", "message_fr", "cta_label", "cta_label_fr", "cta_url", "image"];
const pickFields = (o) => ({
  ...Object.fromEntries(FIELDS.map((k) => [k, o[k] || ""])),
  listings_enabled: !!o.listings_enabled, listings_count: o.listings_count || 6, listings_categories: o.listings_categories || [],
});
const Btn = ({ onClick, disabled, testid, children, danger }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`h-9 px-3 rounded-lg border text-sm font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40 ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-200 text-[#12234A] hover:bg-slate-50"}`}>{children}</button>
);

export function NewsletterTemplates({ f, setF, empty }) {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState("");
  const load = () => api.get("/admin/newsletter/templates").then((r) => setItems(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);
  const cur = items.find((t) => t.id === current);
  const hasContent = FIELDS.some((k) => f[k]);
  useEffect(() => { if (!hasContent) setCurrent(""); }, [hasContent]);

  const use = (id) => {
    const t = items.find((x) => x.id === id);
    if (!t) return;
    if (hasContent && JSON.stringify(pickFields(f)) !== JSON.stringify(pickFields(t)) && !window.confirm(`Replace what you've written with “${t.name}”?`)) return;
    setCurrent(id);
    setF({ ...empty, ...pickFields(t) });
    toast.success(`Loaded “${t.name}” — edit anything before sending`);
  };
  const saveNew = async () => {
    const name = window.prompt("Name this template", cur ? `${cur.name} (copy)` : f.subject)?.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/admin/newsletter/templates", { ...pickFields(f), name });
      toast.success(`Saved template “${data.name}”`);
      await load();
      setCurrent(data.id);
    } catch (e) { toast.error(errText(e)); }
  };
  const update = async () => {
    if (!window.confirm(`Overwrite template “${cur.name}” with what's in the form now?`)) return;
    try { await api.put(`/admin/newsletter/templates/${cur.id}`, { ...pickFields(f), name: cur.name }); toast.success("Template updated"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete template “${cur.name}”? Newsletters already sent are not affected.`)) return;
    try { await api.delete(`/admin/newsletter/templates/${cur.id}`); toast.success("Template deleted"); setCurrent(""); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="mb-4 rounded-xl bg-slate-50 border border-slate-200 p-3 flex flex-wrap items-center gap-2" data-testid="nl-templates">
      <LayoutTemplate size={16} className="text-[#F5551E]" />
      <Select value={current} onValueChange={use}>
        <SelectTrigger className="h-9 w-72 rounded-lg bg-white" data-testid="nl-template-select"><SelectValue placeholder={items.length ? "Start from a template…" : "No templates yet"} /></SelectTrigger>
        <SelectContent className="bg-white">
          {items.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`nl-template-${t.id}`}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Btn onClick={saveNew} disabled={!f.subject || !f.message} testid="nl-template-save-new"><BookmarkPlus size={14} /> Save as template</Btn>
      {cur && <Btn onClick={update} disabled={!f.subject || !f.message} testid="nl-template-update"><Save size={14} /> Update “{cur.name.length > 24 ? `${cur.name.slice(0, 24)}…` : cur.name}”</Btn>}
      {cur && <Btn onClick={remove} testid="nl-template-delete" danger><Trash2 size={14} /> Delete</Btn>}
    </div>
  );
}
